import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Linkedin, Twitter, Mail } from 'lucide-react';
import SidebarNavigation from '../components/SidebarNavigation';
import { isAuthenticated } from '../lib/auth';

const About = () => {
  const isAuth = isAuthenticated();

  return (
    <div className="min-h-screen bg-gray-50">
      {isAuth && <SidebarNavigation />}
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Hero Section */}
          <div 
            className="h-64 bg-cover bg-center relative"
            style={{
              backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(147, 51, 234, 0.8)), url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80')`
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <h1 className="text-5xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                About DigiDreamSolutions
              </h1>
            </div>
          </div>

          <div className="p-8 md:p-12">
            {/* Mission Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                At DigiDreamSolutions, we're on a mission to empower businesses with cutting-edge digital tools 
                that simplify operations, enhance collaboration, and drive growth. We believe that every business, 
                regardless of size, deserves access to professional-grade software that's both powerful and easy to use.
              </p>
            </section>

            {/* Founder Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Meet Our Founder</h2>
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-6xl font-bold">
                  DS
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">DigiDreamSolutions Team</h3>
                  <p className="text-blue-600 font-medium mb-4">Founder & CEO</p>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    With over a decade of experience in software development and business solutions, our team 
                    founded DigiDreamSolutions to bridge the gap between complex enterprise software and the 
                    needs of modern businesses. We're passionate about creating tools that make work easier, 
                    more efficient, and more enjoyable.
                  </p>
                  <div className="flex gap-4">
                    <a href="#" className="text-slate-600 hover:text-blue-600 transition-colors">
                      <Linkedin className="w-6 h-6" />
                    </a>
                    <a href="#" className="text-slate-600 hover:text-blue-600 transition-colors">
                      <Twitter className="w-6 h-6" />
                    </a>
                    <a href="mailto:founder@digidreamsolutions.com" className="text-slate-600 hover:text-blue-600 transition-colors">
                      <Mail className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Values Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Values</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Innovation</h3>
                  <p className="text-blue-800">
                    We continuously push boundaries to deliver cutting-edge solutions that stay ahead of the curve.
                  </p>
                </div>
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-purple-900 mb-2">Simplicity</h3>
                  <p className="text-purple-800">
                    Complex problems deserve elegant solutions. We make powerful tools that anyone can use.
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-green-900 mb-2">Customer Success</h3>
                  <p className="text-green-800">
                    Your success is our success. We're committed to helping you achieve your business goals.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Get in Touch</h2>
              <p className="text-lg mb-6">
                Have questions or want to learn more? We'd love to hear from you!
              </p>
              <a 
                href="mailto:hello@digidreamsolutions.com"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Contact Us
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;